import BackButtonToolbar from "features/BackButtonToolbar";
import React, { useEffect, useState } from "react";
import {
  ArrayInput,
  AutocompleteInput,
  BooleanInput,
  FormDataConsumer,
  Labeled,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceInput,
  required,
  ResourceContextProvider,
  SelectArrayInput,
  SelectInput,
  SimpleForm,
  SimpleFormIterator,
  TextField,
  Title,
  useNotify,
  useRedirect,
  useStore,
} from "react-admin";
import { useLocation } from "react-router";
import dataProvider from "server/dataProvider";
import { StudentServicePath, WEEKLY_STREAM_SCHEDULE } from "utils/constants";
import { studentRequests } from "api/studentApi";
import { CircularProgress, Typography } from "@mui/material";
import { Box, Grid } from "@mui/material";
import { administrationRequests } from "api/administrationApi";
import { FromWeekToWeekChoices, WeekDaysChoices, WeekTypeChoices } from "utils/choices";
import CustomTimeOnlyInput from "server/components/Fields/CustomTimeOnlyInput";
import StudyGroupField from "server/components/Fields/StudyGroupField";
import { settings } from "app";
import ProfessorInput from "server/components/Fields/ProfessorInput";
import { validateFromToWeek, validateTimeStart } from "components/LearningActivity/CreateWeeklySchedule/utils/utils";
import useConflictErrorModal from "server/components/Modals/useConflictErrorModal";
import { useNotifyError } from "utils/useNotifyError";
import CurriculumStudyGroupsInput from "../components/CurriculumStudyGroupsInput";
import { hydrateStudyGroupsForm, normalizeStudyGroupsPayload } from "../components/utils";
import { STREAMS_FILTERS_KEY } from "../List";
import { useFormContext } from "react-hook-form";

// Rooms are filtered by class type, so switching it invalidates the room already picked.
const ClassTypeInput = props => {
  const { setValue } = useFormContext();

  return (
    <SelectInput
      {...props}
      onChange={() => setValue("roomId", null)}
    />
  );
};

const EditStreamWeeklySchedule = () => {
  const { handleConflictError, ConflictErrorDialog } = useConflictErrorModal();
  const notifyError = useNotifyError();
  const notify = useNotify();
  const redirect = useRedirect();
  const location = useLocation();
  const { groupId } = location.state;

  const [disciplineId, setDisciplineId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const [storedFilters] = useStore(STREAMS_FILTERS_KEY, null);
  const academicSessionType = storedFilters?.sessionType;

  useEffect(() => {
    const fetch = async () => {
      if (!groupId) return;
      setIsLoading(true);
      try {
        const { data } = await dataProvider.customRequest(
          studentRequests.getStreamWeeklyScheduleForUpdate(groupId),
          null,
          "GET",
        );
        setEditData(hydrateStudyGroupsForm(data || {}));
        setDisciplineId(data?.disciplineId || "");
      } catch (e) {
        notifyError(e);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, [groupId, notifyError]);

  const handleSubmit = async values => {
    const payload = { schedule: normalizeStudyGroupsPayload(values) };
    try {
      await dataProvider.customRequest(studentRequests.updateStreamWeeklySchedule(), payload, "PUT");
      notify("Разписанието беше успешно обновено", { type: "success" });
      redirect(-1);
    } catch (err) {
      notifyError(err);
      handleConflictError(err);
    }
  };

  return (
    <>
      <Title title="Редактиране на занятия в поток" />
      <BackButtonToolbar />
      <ResourceContextProvider value={`${StudentServicePath}${WEEKLY_STREAM_SCHEDULE}`}>
        {isLoading ? (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress size={200} />
          </Box>
        ) : (
          <>
            <Grid
              container
              sx={{ ml: 2, mb: 3, gap: 3 }}
            >
              <Labeled label="Дисциплина">
                <ReferenceField
                  source="id"
                  reference={administrationRequests.getDisciplinesSelect()}
                  record={{ id: disciplineId }}
                >
                  <TextField source="name" />
                </ReferenceField>
              </Labeled>

              {academicSessionType && (
                <Labeled label="Семестър">
                  <ReferenceField
                    source="id"
                    reference={studentRequests.getAcademicSessionTypesSelect()}
                    record={{ id: academicSessionType }}
                  >
                    <TextField source="name" />
                  </ReferenceField>
                </Labeled>
              )}
            </Grid>
            <Typography
              color="secondary"
              fontWeight="bold"
              ml={2}
            >
              Разписание
            </Typography>
            <SimpleForm
              defaultValues={editData}
              onSubmit={handleSubmit}
            >
              <Grid
                container
                spacing={2}
              >
                <Grid
                  item
                  md={6}
                  sm={6}
                  xs={12}
                >
                  <ReferenceArrayInput
                    source="curriculumIds"
                    reference={studentRequests.getCurriculumsSelect()}
                    filter={{
                      disciplineId: disciplineId,
                      disciplineInSemesterType: academicSessionType,
                      UsesElectiveAndFacultative: true,
                    }}
                  >
                    <SelectArrayInput
                      label="Учебни планове"
                      validate={[required()]}
                    />
                  </ReferenceArrayInput>
                </Grid>
                <Grid
                  item
                  md={3}
                >
                  <ReferenceInput
                    source="classTypeId"
                    reference={administrationRequests.getRoomClassTypesSelect()}
                  >
                    <ClassTypeInput
                      optionText="name"
                      label="Тип занятия"
                      validate={[required()]}
                    />
                  </ReferenceInput>
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <SelectInput
                    source="weekType"
                    label="Тип седмица"
                    choices={WeekTypeChoices}
                    validate={[required()]}
                    fullWidth
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={3}
                >
                  <SelectInput
                    source="weekDay"
                    label="Ден от седмицата"
                    choices={WeekDaysChoices}
                    validate={[required()]}
                    fullWidth
                  />
                </Grid>
              </Grid>
              <Grid
                container
                spacing={2}
              >
                <FormDataConsumer>
                  {({ formData }) =>
                    formData?.classTypeId && (
                      <Grid
                        item
                        xs={12}
                        md={4}
                      >
                        <ReferenceInput
                          source="roomId"
                          reference={administrationRequests.getRoomsSelect()}
                          filter={{ classTypes: formData.classTypeId }}
                        >
                          <AutocompleteInput
                            label="Зала"
                            optionValue="id"
                            optionText={rec =>
                              `${rec.roomName} (Номер на зала: ${rec.roomNumber || ""}) ${
                                rec.buildingName
                              } (Капацитет занятия: ${rec.capacityClasses || ""})`
                            }
                            filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
                            validate={[required()]}
                            fullWidth
                          />
                        </ReferenceInput>
                      </Grid>
                    )
                  }
                </FormDataConsumer>
                <FormDataConsumer>
                  {({ formData }) => (
                    <>
                      <Grid
                        item
                        xs={12}
                        md={2}
                      >
                        <CustomTimeOnlyInput
                          source="startTime"
                          label="Начален час"
                          validate={[required(), () => validateTimeStart(formData)]}
                          required
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={2}
                      >
                        <CustomTimeOnlyInput
                          source="endTime"
                          label="Краен час"
                          validate={[required(), () => validateTimeStart(formData)]}
                          required
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={2}
                      >
                        <SelectInput
                          source="fromWeek"
                          label="От седмица"
                          choices={FromWeekToWeekChoices}
                          validate={[required(), () => validateFromToWeek(formData)]}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={2}
                      >
                        <SelectInput
                          source="toWeek"
                          label="До седмица"
                          choices={FromWeekToWeekChoices}
                          validate={[required(), () => validateFromToWeek(formData)]}
                        />
                      </Grid>
                    </>
                  )}
                </FormDataConsumer>
              </Grid>
              <FormDataConsumer>
                {({ formData }) => (
                  <Grid
                    container
                    spacing={2}
                    mb={2}
                  >
                    <Grid
                      item
                      xs={12}
                    >
                      <BooleanInput
                        source="perCurriculumStudyGroups"
                        label="Различни учебни групи по учебен план"
                        helperText={false}
                      />
                    </Grid>
                    {formData?.perCurriculumStudyGroups ? (
                      <Grid
                        item
                        xs={12}
                      >
                        <CurriculumStudyGroupsInput curriculumIds={formData?.curriculumIds || []} />
                      </Grid>
                    ) : (
                      <Grid
                        item
                        xs={12}
                        md={4}
                      >
                        <StudyGroupField
                          label=" Пример за въвеждане на Учебни групи: 1-5, 8, 11-13, 15. Оставете празно ако разписанието се отнася за всички
        групи!"
                        />
                      </Grid>
                    )}
                  </Grid>
                )}
              </FormDataConsumer>

              <ArrayInput
                source="professors"
                label="Преподаватели"
              >
                <SimpleFormIterator
                  disableReordering
                  disableClear
                  sx={settings.style.iteratorWithoutGap}
                >
                  <Grid
                    container
                    spacing={2}
                  >
                    <Grid
                      item
                      xs={12}
                      md={12}
                    >
                      <ProfessorInput
                        source="employeeId"
                        label="Преподавател"
                        fullWidth
                        validate={[required()]}
                        filters={{ disciplineId: disciplineId }}
                      />
                    </Grid>
                  </Grid>
                  <Grid
                    container
                    spacing={2}
                  >
                    <FormDataConsumer>
                      {({ scopedFormData }) => (
                        <>
                          <Grid
                            item
                            xs={12}
                            md={4}
                          >
                            <SelectInput
                              source="fromWeek"
                              label="От седмица"
                              choices={FromWeekToWeekChoices}
                              validate={[required(), () => validateFromToWeek(scopedFormData)]}
                            />
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={4}
                          >
                            <SelectInput
                              source="toWeek"
                              label="До седмица"
                              choices={FromWeekToWeekChoices}
                              validate={[required(), () => validateFromToWeek(scopedFormData)]}
                            />
                          </Grid>
                        </>
                      )}
                    </FormDataConsumer>
                  </Grid>
                </SimpleFormIterator>
              </ArrayInput>
            </SimpleForm>
            <ConflictErrorDialog />
          </>
        )}
      </ResourceContextProvider>
    </>
  );
};

export default EditStreamWeeklySchedule;
